/**
 * Post-Stripe-checkout success page. Polls /checkout/status until paid.
 */
import React, { useCallback, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { useI18n } from "@/contexts/I18nContext";
import { api } from "@/lib/api";
import { useImperativeLoad } from "@/hooks/useImperativeLoad";
import { CheckCircle2, Loader2, BookOpen, Store } from "lucide-react";

export default function MarketplaceSuccess() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("pending");
  const [purchase, setPurchase] = useState(null);

  const poll = useCallback(async () => {
    if (!sessionId) return;
    try {
      const r = await api.get(`/marketplace/checkout/status/${sessionId}`);
      const s = r.data.payment_status || r.data.status || "pending";
      setStatus(s);
      if (r.data.payment_status === "paid") {
        setPurchase(r.data);
      }
    } catch (e) {
      setStatus("error");
    }
  }, [sessionId]);

  // Use imperative-load pattern matching the rest of the codebase to avoid the
  // react-hooks/set-state-in-effect lint warning.
  useImperativeLoad(() => {
    if (!sessionId) {
      navigate("/marketplace");
      return;
    }
    poll();
    const interval = setInterval(() => { poll(); }, 3000);
    return () => clearInterval(interval);
  }, [sessionId]);

  return (
    <div className="flex min-h-full items-center justify-center bg-[#0A0C1F] p-6 text-white" data-testid="marketplace-success-page">
      <Card className="w-full max-w-lg border-slate-800 bg-slate-900/80 p-10 text-center">
        {status === "paid" ? (
          <>
            <CheckCircle2 className="mx-auto h-16 w-16 text-[#9ACD32]" />
            <h1 className="mt-5 font-display text-3xl font-extrabold text-white">
              {t("marketplace.success_title")}
            </h1>
            <p className="mt-2 text-sm text-slate-400">{t("marketplace.success_body")}</p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
              <Link
                to="/marketplace/library"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#D4A937] px-5 py-3 text-sm font-bold text-[#05073B] hover:bg-[#E0B850]"
                data-testid="success-cta-library"
              >
                <BookOpen className="h-4 w-4" /> {t("marketplace.success_cta_library")}
              </Link>
              <Link
                to="/marketplace"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-700 px-5 py-3 text-sm font-bold text-slate-200 hover:bg-slate-800"
                data-testid="success-cta-marketplace"
              >
                <Store className="h-4 w-4" /> {t("marketplace.success_cta_marketplace")}
              </Link>
            </div>
            {purchase?.template_id && (
              <p className="mt-5 font-mono text-[10px] uppercase tracking-wider text-slate-500">
                purchase_id: {purchase.purchase_id}
              </p>
            )}
          </>
        ) : (
          <>
            <Loader2 className="mx-auto h-16 w-16 animate-spin text-[#9ACD32]" />
            <h1 className="mt-5 font-display text-2xl font-extrabold text-white">
              {t("marketplace.success_polling")}
            </h1>
          </>
        )}
      </Card>
    </div>
  );
}
